import { z } from "zod";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { GenericFieldArray } from "@/lib/Components/Form/FieldArray/GenericFieldArray";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { updateTripFaqsMutation } from "@/app/Cities/Trips/GraphQL/tripRecordQuery";
import { useTripRecord } from "@/app/Cities/Trips/Hooks/useTripRecord";

const validationSchema = z.object({
  faqs: z.array(
    z.object({
      question: z
        .string()
        .trim()
        .min(1, { error: "Enter a question" })
        .max(500),
      answer: z.string().trim().min(1, { error: "Enter an answer" }).max(5000),
    }),
  ),
});

type FormValues = z.infer<typeof validationSchema>;

export function TripFaqsScreen() {
  const trip = useTripRecord();
  const { mutateAsync } = useGqlMutation(updateTripFaqsMutation);

  return (
    <div className="py-6">
      <RecordScreenFormCard<FormValues>
        title="FAQs"
        subtitle={`Shown on the ${trip.cityA.name} to ${trip.cityB.name} and ${trip.cityB.name} to ${trip.cityA.name} deal pages, in this order. With none, those pages show the regional FAQs.`}
        validationSchema={toFormikValidationSchema(validationSchema)}
        initialValues={{
          faqs: trip.faqs.map(({ question, answer }) => ({ question, answer })),
        }}
        onSubmit={async ({ faqs }) => {
          await mutateAsync({ id: trip.id, faqs });
        }}
        view={
          trip.faqs.length ? (
            <dl className="col-span-full divide-y divide-gray-100">
              {trip.faqs.map((faq) => (
                <div key={faq.id} className="py-4">
                  <dt className="font-medium text-gray-900">{faq.question}</dt>
                  <dd className="mt-1 whitespace-pre-line text-sm text-gray-600">
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="col-span-full py-6 text-center text-sm text-gray-500">
              No route FAQs yet. The deal pages show the regional FAQs.
            </p>
          )
        }
      >
        <GenericFieldArray<FormValues["faqs"][number]>
          fieldArrayKey="faqs"
          lineItemLabel="FAQ"
          Icon={QuestionMarkCircleIcon}
          newLineItemValues={{ question: "", answer: "" }}
        >
          {(_, index) => (
            <>
              <TextInput
                name={`faqs.${index}.question`}
                label="Question"
                className="col-span-full"
              />
              <TextAreaInput
                name={`faqs.${index}.answer`}
                label="Answer"
                className="col-span-full"
                textAreaProps={{ rows: 4 }}
              />
            </>
          )}
        </GenericFieldArray>
      </RecordScreenFormCard>
    </div>
  );
}
